import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "../components/ui/SectionHeader";
import { LoadingCard } from "../components/ui/LoadingPlaceholder";
import { services } from "../data/siteData";
import { fetchRealizations, fetchSiteContent } from "../services/publicApi";

const defaultContent = {
  heroEyebrow: "ELTT elektroinstalace",
  heroTitle: "Spolehliva elektro prace bez zbytecneho chaosu.",
  heroText: "Elektroinstalace, rekonstrukce, revize a servis pro domy, byty a mensi firmy. Jasna domluva, ciste predani a dokumenty pohromade.",
  heroImageUrl: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=85",
  heroImageAlt: "Elektroinstalace v rozvadeci",
  servicesKicker: "Co delame",
  servicesTitle: "Hlavni sluzby",
  servicesText: "Prehled nejcastejsich elektro praci pro rodinne domy, byty a mensi provozy.",
  processKicker: "Postup",
  processTitle: "Jednoduchy proces",
  processText: "Nejdriv kratka domluva, potom prohlidka nebo naceneni, realizace a predani dokumentu.",
};

function HomePage() {
  const [realizations, setRealizations] = useState([]);
  const [siteContent, setSiteContent] = useState(defaultContent);
  const [siteServices, setSiteServices] = useState(services);
  const [realizationsStatus, setRealizationsStatus] = useState({ type: "loading", message: "" });

  useEffect(() => {
    let mounted = true;

    async function loadRealizations() {
      setRealizationsStatus({ type: "loading", message: "" });
      try {
        const response = await fetchRealizations();
        if (!mounted) return;
        setRealizations(response?.items || []);
        setRealizationsStatus({ type: "idle", message: "" });
      } catch (error) {
        if (!mounted) return;
        setRealizationsStatus({ type: "error", message: "Realizace se teď nepodařilo načíst." });
      }
    }

    loadRealizations();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadSiteContent() {
      try {
        const response = await fetchSiteContent();
        if (!mounted) return;
        setSiteContent({ ...defaultContent, ...(response?.content || {}) });
        setSiteServices(response?.services?.length ? response.services : services);
      } catch (error) {
        if (!mounted) return;
        setSiteContent(defaultContent);
        setSiteServices(services);
      }
    }

    loadSiteContent();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <section className="surface">
        <div className="shell grid gap-10 py-14 sm:py-20 lg:grid-cols-[1fr_.9fr] lg:items-center lg:py-24">
          <div>
            <p className="eyebrow">{siteContent.heroEyebrow}</p>
            <h1 className="mt-4 max-w-3xl text-5xl font-extrabold tracking-tight text-white sm:text-6xl">
              {siteContent.heroTitle}
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-8 text-slate-300">
              {siteContent.heroText}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/kontakt" className="btn-primary">
                Poslat poptávku
              </Link>
              <Link to="/sluzby" className="btn-secondary">
                Zobrazit služby
              </Link>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-cyan-300/15 bg-[#06111b]">
            <img
              src={siteContent.heroImageUrl}
              alt={siteContent.heroImageAlt}
              className="h-72 w-full object-cover sm:h-96 lg:h-[440px]"
            />
          </div>
        </div>
      </section>

      <section className="page-section surface-soft">
        <div className="shell">
          <SectionHeader kicker={siteContent.servicesKicker} title={siteContent.servicesTitle}>
            {siteContent.servicesText}
          </SectionHeader>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {siteServices.map((service) => (
              <article key={service.title} className="card p-6">
                <h2 className="text-xl font-bold text-white">{service.title}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-300">{service.description}</p>
                <p className="mt-5 text-sm font-semibold text-cyan-300">{service.price}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="surface py-12 sm:py-16">
        <div className="shell grid gap-8 lg:grid-cols-[.85fr_1fr] lg:items-center">
          <SectionHeader kicker={siteContent.processKicker} title={siteContent.processTitle}>
            {siteContent.processText}
          </SectionHeader>
          <div className="grid gap-4 sm:grid-cols-3">
            {["Poptavka", "Nabidka", "Realizace"].map((step, index) => (
              <div key={step} className="card p-5">
                <div className="text-sm font-semibold text-cyan-300">0{index + 1}</div>
                <div className="mt-2 font-bold text-white">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section surface-soft">
        <div className="shell">
          <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
            <SectionHeader kicker="Realizace" title="Ukázky práce" />
            <Link to="/realizace" className="btn-secondary">
              Všechny realizace
            </Link>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {realizationsStatus.type === "loading" && !realizations.length
              ? Array.from({ length: 3 }).map((_, index) => <LoadingCard key={`home-realization-loading-${index}`} />)
              : realizations.slice(0, 3).map((item) => (
              <article key={item.title} className="card overflow-hidden">
                <img src={item.image} alt={item.title} className="h-48 w-full object-cover" />
                <div className="p-5">
                  <h2 className="font-bold text-white">{item.title}</h2>
                  <p className="mt-1 text-sm text-slate-400">{item.place}</p>
                </div>
              </article>
            ))}
          </div>
          {realizationsStatus.type === "error" && (
            <p className="mt-5 text-sm text-slate-400">{realizationsStatus.message}</p>
          )}
        </div>
      </section>
    </>
  );
}

export default HomePage;
